<template>
    <div class="scheduled-services-calendar">
        <div class="row">
            <div class="col-md-12">
                <div class="card mb-4">
                    <div class="card-header d-flex justify-content-between align-items-center p-2 text-black">
                        <div class="d-flex align-items-center gap-3">
                                <div class="d-flex gap-1">
                                    <PreviousDateIcon v-model="currentDate" decrement="month" />
                                    <NextDateIcon v-model="currentDate" increment="month"/>
                                    <CalendarIcon
                                        v-model="currentDate"
                                        :auto-apply="true"
                                        :size="23"
                                        :monthPicker="true"
                                    />
                                </div>
                                <div>
                                    Scheduled services ({{ dataTimeFormat(currentDate, 'MMMM, YYYY')}})
                                </div>
                        </div>
                        <div class="d-flex gap-1">
                            <div class="d-flex align-items-center gap-1">
                                <input type="text" class="form-control form-control-sm" placeholder="Search..." v-model="filters.search">
                            </div>
                            <div class="d-flex align-items-center gap-1">
                                <button class="btn btn-sm btn-outline-theme" @click="goToToday">Today</button>
                                <button class="btn btn-sm btn-outline-theme" @click="goToYesterday">Yesterday</button>
                                <button class="btn btn-sm btn-outline-danger" @click="resetCalendar">Reset</button>
                            </div>
                        </div>
                    </div>

                    <div class="card-body p-0">

                        <!-- Calendar Grid -->
                        <div class="calendar-container">
                            <div class="calendar-grid">
                                <!-- Day Headers -->
                                <div class="bg-theme border-1 p-2 text-center border-end fw-bold">Mon</div>
                                <div class="bg-theme border-1 p-2 text-center border-end fw-bold">Tue</div>
                                <div class="bg-theme border-1 p-2 text-center border-end fw-bold">Wed</div>
                                <div class="bg-theme border-1 p-2 text-center border-end fw-bold">Thu</div>
                                <div class="bg-theme border-1 p-2 text-center border-end fw-bold">Fri</div>
                                <div class="bg-theme border-1 p-2 text-center border-end fw-bold">Sat</div>
                                <div class="bg-theme border-1 p-2 text-center border-end fw-bold">Sun</div>

                                <!-- Calendar Days -->
                                <div
                                    v-for="day in calendarDays"
                                    :key="day.date"
                                    :class="['day-cell', {
                                        'other-month': !day.currentMonth,
                                        'today': day.isToday,
                                        'selected': day.isSelected
                                    }]"
                                    @click="selectDate(day)"
                                >
                                    <div class="day-number">{{ day.dayNumber }}</div>
                                    <div class="day-events">
                                        <div
                                            v-for="event in day.events"
                                            :key="event.id"
                                            :class="['event-item', `event-${event.status}`]"
                                            data-bs-html="true"
                                            v-tooltip.top="getTooltipContent(event)"
                                        >
                                            <span class="event-name">{{ event.title }}</span>
                                            <span class="event-address">{{ event.address }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Selected Date Details -->
                <div v-if="selectedDate && getEventsForDate(selectedDate).length" class="mt-4" ref="selectedDateServices">
                    <h5>Services on {{ dataTimeFormat(selectedDate, 'dddd, MMMM D, YYYY') }}</h5>
                    <div class="row">
                        <div v-for="event in getEventsForDate(selectedDate)" :key="event.id" class="col-md-4">
                            <div :class="['card', `border-${event.statusColor}`]">
                                <div class="card-body">
                                    <h6 class="card-title mb-2">{{ event.customerName }}</h6>
                                    <p class="card-text mb-1">
                                        <strong>Service:</strong> {{ event.serviceName }}
                                    </p>
                                    <p class="card-text mb-1">
                                        <strong>Address:</strong> {{ event.fullAddress }}
                                    </p>
                                    <p class="card-text mb-1">
                                        <strong>Time:</strong> {{ event.time }}
                                    </p>
                                    <span :class="['badge', `bg-${event.statusColor}`]">{{ event.status }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
// Import the directive
import { tooltipDirective } from '../../directives/tooltip';
import { appMixin } from '../../mixin/appMixin';
import NextDateIcon from '../partials/NextDateIcon.vue';
import PreviousDateIcon from '../partials/PreviousDateIcon.vue';
import CalendarIcon from '../partials/CalendarIcon.vue';


export default {
    name: 'ScheduledServicesCalendarView',
    mixins: [appMixin],
    directives: {
        tooltip: tooltipDirective
    },
    components: {
        NextDateIcon,
        PreviousDateIcon,
        CalendarIcon
    },
    data() {
        return {
            currentDate: this.momentWithoutUTC(new Date()),
            selectedDate: null,
            filters: {
                search: ''
            },
            searchTimer: null,
            services: []
        };
    },
    mounted() {
        // Set today as default selected date
        this.selectedDate = this.momentWithoutUTC(new Date());

        // Initial fetch of scheduled services
        this.getScheduledServices();
    },
    computed: {
        currentMonth() {
            let safeDate = this.currentDate;
            if (!safeDate || !safeDate.isMoment) {
                safeDate = this.momentWithoutUTC(safeDate || new Date());
            }
            return safeDate.format('MMMM');
        },
        currentYear() {
            let safeDate = this.currentDate;
            if (!safeDate || !safeDate.isMoment) {
                safeDate = this.momentWithoutUTC(safeDate || new Date());
            }
            return safeDate.format('YYYY');
        },
        calendarDays() {
            const days = [];
            // Safely convert currentDate to moment if needed
            let safeDate = this.currentDate;
            if (!safeDate || !safeDate.isMoment) {
                safeDate = this.momentWithoutUTC(safeDate || new Date());
            }

            const startOfMonth = safeDate.clone().startOf('month');
            const endOfMonth = safeDate.clone().endOf('month');

            // Get the first day of month
            let current = startOfMonth.clone();

            // Go back to the previous Monday (0 = Sunday, 1 = Monday, etc.)
            while (current.day() !== 1) {
                current.subtract(1, 'day');
            }

            console.log('Generating calendar for:', safeDate.format('MMMM YYYY'));
            console.log('Calendar starts on:', current.format('YYYY-MM-DD'));
            console.log('Calendar ends on:', endOfMonth.format('YYYY-MM-DD'));
            console.log('Selected date is:', this.selectedDate ? this.selectedDate.format('YYYY-MM-DD') : 'None');
            // Generate calendar days until we pass the end of month and reach a Sunday
            while (current.isBefore(endOfMonth, 'day') || current.day() !== 0) {
                const dateStr = current.format('YYYY-MM-DD');
                const day = {
                    date: dateStr,
                    dayNumber: current.date(),
                    currentMonth: current.month() === safeDate.month(),
                    isToday: current.isSame(this.momentWithoutUTC(new Date()), 'day'),
                    isSelected: this.selectedDate && current.isSame(this.selectedDate, 'day'),
                    events: this.getEventsForDate(dateStr)
                };
                days.push(day);
                current.add(1, 'day');
            }

            return days;
        }
    },
    watch: {
        currentDate: {
            handler(newDate, oldDate) {
                // If currentDate is a string, convert it to moment
                if (newDate && typeof newDate === 'string') {
                    // Parse the string as a full date, not just month
                    const parsed = this.momentWithoutUTC(newDate);
                    // Update currentDate with the moment object (this won't trigger the watcher again because we're setting the same ref)
                    this.$nextTick(() => {
                        if (this.currentDate === newDate) {
                            this.currentDate = parsed;
                        }
                        this.getScheduledServices();
                    });
                    return;
                }
                // Reset selected date when month changes
                this.selectedDate = null;
                this.getScheduledServices();
            },
            deep: true
        },
        // Debounce search: fetch when user stops typing
        'filters.search': {
            handler(newVal) {
                // clear previous timer
                if (this.searchTimer) {
                    clearTimeout(this.searchTimer);
                }
                // set debounce (500ms)
                this.searchTimer = setTimeout(() => {
                    // reset page/selection if needed
                    this.selectedDate = null;
                    this.getScheduledServices();
                    this.searchTimer = null;
                }, 500);
            },
            immediate: false
        }
    },
    beforeUnmount() {
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
            this.searchTimer = null;
        }
    },
    methods: {
        getEventsForDate(date) {
            const dateStr = typeof date === 'string' ? date : this.momentWithoutUTC(date).format('YYYY-MM-DD');


            // Filter services by next_due_date
            return this.services
                .filter(service => {
                    const serviceDate = this.momentWithoutUTC(service.next_due_date).format('YYYY-MM-DD');
                    return serviceDate === dateStr;
                })
                .map(service => {
                    // Build address string: address, address_1 (if not null), postal_code
                    const addressParts = [service.address?.address];
                    if (service.address?.address_1) {
                        addressParts.push(service.address.address_1);
                    }
                    if (service.address?.postal_code) {
                        addressParts.push(service.address.postal_code);
                    }
                    const formattedAddress = addressParts.filter(Boolean).join(', ');

                    return {
                        id: service.id,
                        date: this.momentWithoutUTC(service.next_due_date).format('YYYY-MM-DD'),
                        title: service.customer?.name || 'Service',
                        customerName: service.customer?.name || 'N/A',
                        serviceName: service.service?.name || 'N/A',
                        address: formattedAddress || 'N/A',
                        fullAddress: service.address?.full_address || 'N/A',
                        time: (() => {
                            let momentDate = this.momentWithoutUTC(service.next_due_date);
                            let timeStr = momentDate.format('hh:mm A');
                            if (momentDate.hour() === 0 && momentDate.minute() === 0) {
                                timeStr = '7:00 AM';
                            }
                            return timeStr;
                        })(),
                        status: service.status || 'Scheduled',
                        statusColor: this.getStatusColor(service.status)
                    };
                });
        },
        getStatusColor(status) {
            const colorMap = {
                'scheduled': 'success',
                'confirmed': 'info',
                'pending': 'warning',
                'completed': 'success',
                'active': 'info',
                'cancelled': 'danger',
                'declined': 'danger'
            };
            return colorMap[status?.toLowerCase()] || 'secondary';
        },
        getTooltipContent(event) {
            return `
                <div class="tooltip-content">
                    <strong>${event.title}</strong><br>
                    <small>
                        <strong>Service:</strong> ${event.serviceName}<br>
                        <strong>Address:</strong> ${event.fullAddress}<br>
                        <strong>Date:</strong> ${this.momentWithoutUTC(event.date).format('dddd, MMMM D, YYYY')}<br>
                        <strong>Time:</strong> ${event.time}<br>
                        <strong>Status:</strong> <span class="badge bg-${event.statusColor}">${event.status}</span>
                    </small>
                </div>
            `;
        },
        previousMonth() {
            this.currentDate = this.currentDate.clone().subtract(1, 'month');
        },
        nextMonth() {
            this.currentDate = this.currentDate.clone().add(1, 'month');
        },
        goToToday() {
            this.currentDate = this.momentWithoutUTC(new Date());
        },
        goToYesterday() {
            this.currentDate = this.momentWithoutUTC(new Date()).subtract(1, 'day');
        },
        resetCalendar() {
            this.currentDate = this.momentWithoutUTC(new Date());
            this.selectedDate = null;
        },
        selectDate(day) {
            console.log('Selected day:', day);
            this.selectedDate = this.momentWithoutUTC(day.date);
            console.log('Updated selectedDate to:', this.selectedDate.format('YYYY-MM-DD'));

            // Scroll to selected date services
            this.$nextTick(() => {
                const container = this.$refs.selectedDateServices;
                if (container) {
                    container.scrollIntoView({ behavior: 'smooth' });
                }
            });
        },
        getScheduledServices() {
            // This method can be used to fetch events from an API if needed
            axios.post('/api/admin/scheduled-services/list', {
                month: this.dataTimeFormat(this.currentDate, 'YYYY-MM-DD'),
                search_term: this.filters.search,
                customer_id: null,
                address_id: null,
                services: [],
                status: 'active'
            })
            .then(response => {
                // Process response data if needed
                this.services = response.data.scheduled_services || [];
            })
            .catch(error => {
                console.error(error);
            });
        }
    }
};
</script>

<style scoped>

/* .calendar-container {
    background: white;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    overflow: hidden;
} */

.calendar-grid {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 0;
    background: white;
}

/* .day-header {
    background: #003d7a;
    color: white;
    padding: 12px;
    text-align: center;
    font-weight: 600;
    border: 1px solid #dee2e6;
} */

.day-cell {
    min-height: 150px;
    border: 1px solid #dee2e6;
    padding: 8px;
    background: white;
    cursor: pointer;
    transition: background-color 0.2s;
    overflow-y: auto;
}

.day-cell:hover {
    background-color: #f8f9fa;
}

.day-cell.other-month {
    background-color: #f5f5f5;
    color: #999;
}

.day-cell.today {
    background-color: #e7f3ff;
    border: 2px solid #0066cc;
}

.day-cell.selected {
    background-color: #d4edff;
    border: 2px solid #0066cc;
}

.day-number {
    font-weight: 600;
    margin-bottom: 8px;
    font-size: 14px;
}

.day-events {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.event-item {
    padding: 4px 6px;
    border-radius: 3px;
    font-size: 11px;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.event-item.event-active {
    background-color: #d4edda;
    color: #155724;
    border-left: 3px solid #28a745;
}

.event-item.event-confirmed {
    background-color: #d1ecf1;
    color: #0c5460;
    border-left: 3px solid #17a2b8;
}

.event-item.event-paused {
    background-color: #fff3cd;
    color: #856404;
    border-left: 3px solid #ffc107;
}

.event-item.event-completed {
    background-color: #d4edda;
    color: #155724;
    border-left: 3px solid #28a745;
}

.event-name {
    display: block;
    font-weight: 600;
}

.event-address {
    display: block;
    font-size: 10px;
    opacity: 0.8;
}

/* Responsive */
@media (max-width: 768px) {
    .day-cell {
        min-height: 100px;
    }

    .event-item {
        font-size: 10px;
    }

    .day-header {
        padding: 8px;
        font-size: 12px;
    }
}
</style>
